/**
 * AppearancePresets — class- and NPC-role-based GhostAppearance presets.
 *
 * Characters (players AND NPCs) are humanoids drawn by the one parameterized
 * `GhostRenderer` (shape + color + equipment via GearParts + weapon via
 * WeaponParts + eyes). Previously:
 *   - players fell back to `generateRandomAppearance()` when the server didn't
 *     send an appearance (always, for spawns) → a random, samey ghost; and
 *   - NPCs had NO generator at all → a colored-circle `fallback_npc` + label.
 *
 * These presets give each CLASS a distinct silhouette/palette/weapon, and each
 * NPC ROLE a recognizable look (scholar in a robe, guard in plate, …) — all
 * from the same renderer, no art assets. `GhostRenderer.render` consumes the
 * full appearance surface, so presets differentiate through silhouette,
 * palette, face, equipment, weapon, accessory, aura, trail, and glow.
 */

import type { GhostAppearance, EquipmentConfig, WeaponConfig, ArmorType, HelmetType, WeaponType } from './AppearanceDefinitions';
import { DEFAULT_GHOST } from './AppearanceDefinitions';
import { withNpcEyes } from './npcEyes';

function equip(type: ArmorType, color: string, helmet: HelmetType = 'none'): EquipmentConfig {
  const slots = {
    ...DEFAULT_GHOST.equipment!.slots,
    chest: 1,
    hands: 1,
    feet: 1,
    waist: 1,
    shoulders: type === 'plate' ? 1 : 0,
  };
  return { type, color, helmet, slots };
}

function weapon(type: WeaponType, color: string, accentColor = '#FFD700', variant = 1): WeaponConfig {
  return { type, color, accentColor, variant };
}

/** Base a preset on DEFAULT_GHOST so every required field is present. */
function preset(over: Partial<GhostAppearance>): GhostAppearance {
  return { ...DEFAULT_GHOST, ...over };
}

// ── Player classes ──────────────────────────────────────────────────────
// Distinct shape + body color + armor + weapon per class. Heavy classes use
// `wide` + plate; casters/skirmishers use `slim` + robe/leather.

const CLASS_PRESETS: Record<string, GhostAppearance> = {
  warrior: preset({
    shape: 'wide', color: '#b0523a', eyes: 'angry',
    equipment: equip('plate', '#9aa3ad', 'helm'),
    weapon: weapon('sword', '#d0d4da', '#ffcf66', 1),
  }),
  mage: preset({
    shape: 'slim', color: '#6c5ce7', eyes: 'round', glow: true,
    equipment: equip('robe', '#4b2e83', 'hood'),
    weapon: weapon('staff', '#7a5a3a', '#8be9fd', 1),
  }),
  hunter: preset({
    shape: 'slim', color: '#3aa55d', eyes: 'round',
    equipment: equip('leather', '#355e3b', 'hood'),
    weapon: weapon('bow', '#8a5a2b', '#c8f7a0', 1),
  }),
  paladin: preset({
    shape: 'wide', color: '#d9a521', eyes: 'happy', glow: true,
    equipment: equip('plate', '#e8c84a', 'helm'),
    weapon: weapon('mace', '#cfd2d6', '#fff1a8', 1),
  }),
  rogue: preset({
    shape: 'slim', color: '#2d2d3a', eyes: 'angry',
    equipment: equip('leather', '#1c1c26', 'hood'),
    weapon: weapon('dagger', '#9aa0ad', '#ff6b6b', 1),
  }),
  shaman: preset({
    shape: 'classic', color: '#00B3A4', eyes: 'round', glow: true,
    equipment: equip('leather', '#0d7a70', 'bandana'),
    weapon: weapon('mace', '#3aa89c', '#fff17a', 1),
  }),
  priest: preset({
    shape: 'slim', color: '#F5F5F5', eyes: 'happy', glow: true,
    equipment: equip('robe', '#e8e8e8', 'hood'),
    weapon: weapon('wand', '#d4d4d4', '#ffe9a8', 1),
  }),
  warlock: preset({
    shape: 'slim', color: '#4a1f5c', eyes: 'angry', glow: true,
    equipment: equip('robe', '#2e1338', 'hood'),
    weapon: weapon('staff', '#3a2440', '#a259ff', 1),
  }),
  druid: preset({
    shape: 'classic', color: '#4f7a3d', eyes: 'round',
    equipment: equip('leather', '#5c4a2e', 'hood'),
    weapon: weapon('staff', '#6b5a3a', '#8fd15a', 1),
  }),
};

/** Class-based player appearance (used when the server sends no per-player
 *  appearance). Falls back to DEFAULT_GHOST for unknown classes. */
export function appearanceForClass(heroClass?: string): GhostAppearance {
  return CLASS_PRESETS[(heroClass ?? '').toLowerCase()] ?? preset({ shape: 'classic' });
}

// ── NPC roles ───────────────────────────────────────────────────────────
// Village NPCs are friendly humanoids — mostly unarmed (weapon omitted), softer
// eyes, cloth/leather. Guards/warriors get plate + a weapon so they read as
// martial. Keyed by the NPC `type` (e.g. 'npc_scientist'); the 'npc_' prefix is
// stripped so both 'npc_guard' and 'guard' resolve.

const NPC_PRESETS: Record<string, GhostAppearance> = {
  scientist: preset({ shape: 'slim', color: '#cfe8ea', eyes: 'round', mouth: 'neutral', equipment: equip('robe', '#2a8f9e'), weapon: undefined }),
  explorer: preset({ shape: 'classic', color: '#e0c9a6', eyes: 'happy', equipment: equip('leather', '#8a6d3b'), weapon: undefined }),
  guide: preset({ shape: 'slim', color: '#bcd3ec', eyes: 'round', equipment: equip('robe', '#3a6ea5', 'hood'), weapon: weapon('staff', '#7a5a3a', '#8be9fd', 1) }),
  villager: preset({ shape: 'round', color: '#e8d3b0', eyes: 'happy', mouth: 'smile', equipment: equip('leather', '#9a7b53'), weapon: undefined }),
  merchant: preset({ shape: 'round', color: '#e9c7a0', eyes: 'happy', equipment: equip('leather', '#7a5230'), weapon: undefined }),
  guard: preset({ shape: 'wide', color: '#7f8a99', eyes: 'angry', equipment: equip('plate', '#8a94a3', 'helm'), weapon: weapon('sword', '#d0d4da', '#ffcf66', 1) }),
  warrior: preset({ shape: 'wide', color: '#9a6a4a', eyes: 'angry', equipment: equip('plate', '#7f8a99', 'helm'), weapon: weapon('sword', '#d0d4da', '#ffcf66', 1) }),
  elder: preset({ shape: 'slim', color: '#d8d8e0', eyes: 'sleepy', mouth: 'smile', equipment: equip('robe', '#5a5a72', 'hood'), weapon: undefined }),
};

/** NPC-role appearance for the humanoid NPC sprite generator. Strips the
 *  'npc_' prefix; unknown roles get a plain friendly villager look. */
export function appearanceForNpcRole(npcType?: string): GhostAppearance {
  const key = (npcType ?? '').toLowerCase().replace(/^npc_/, '');
  return withNpcEyes(NPC_PRESETS[key] ?? preset({ shape: 'classic', color: '#d9c3a3', eyes: 'round', mouth: 'neutral', equipment: equip('leather', '#8a7b6a'), weapon: undefined }));
}
